import { Cookies } from 'react-cookie';

import { COOKIE_DOMAIN } from './constants';
import { extractUtmParams, getExpirationHoursByUtmSource, isExcludedPath, isPaidTraffic } from './helper';

import { ParsedUrlQuery } from 'querystring';

/**
 * 기존 쿠키와 현재 UTM 파라미터를 비교하여 쿠키 리셋 필요 여부 확인
 * @param utmQueryParams 현재 UTM 파라미터
 * @returns 쿠키 리셋 필요 여부
 */
const shouldResetUtmCookies = (utmQueryParams: Record<string, string>): boolean => {
  if (Object.keys(utmQueryParams).length === 0) {
    return false;
  }

  const cookies = new Cookies();

  // URL에 있는 UTM 파라미터와 쿠키에 저장된 UTM 값이 다른 경우 체크
  for (const [key, value] of Object.entries(utmQueryParams)) {
    const cookieValue = cookies.get(key);
    if (cookieValue !== value) {
      return true;
    }
  }

  // 쿠키에는 있지만 URL에는 없는 UTM 파라미터가 있는지 체크
  const allCookies = document.cookie.split(';');
  for (const cookie of allCookies) {
    const cookieParts = cookie.split('=');
    const cookieName = cookieParts[0].trim();

    if (cookieName.startsWith('utm_') && !(cookieName in utmQueryParams)) {
      return true;
    }
  }

  return false;
};

/**
 * 모든 UTM 쿠키 삭제
 */
const removeAllUtmCookies = (): void => {
  const cookies = new Cookies();
  const allCookies = document.cookie.split(';');

  allCookies.forEach((cookie) => {
    const cookieParts = cookie.split('=');
    const cookieName = cookieParts[0].trim();

    if (cookieName.startsWith('utm_')) {
      cookies.remove(cookieName, {
        path: '/',
        domain: COOKIE_DOMAIN,
      });
    }
  });
};

/**
 * UTM 쿠키 설정하기
 * @param utmParams UTM 파라미터 객체
 * @param expires 만료 시간
 */
const setUtmCookieValues = (utmParams: ParsedUrlQuery, expires: Date): void => {
  const cookies = new Cookies();

  Object.entries(utmParams).forEach(([key, value]) => {
    if (key.startsWith('utm_') && value !== undefined) {
      cookies.set(key, encodeURI(String(value)), {
        path: '/',
        domain: COOKIE_DOMAIN,
        expires,
        httpOnly: false,
      });
    }
  });
};

/**
 * utm_* 쿠키 설정 함수
 * @param utmParams utm_* 형식의 키를 가진 파라미터 객체
 * @param expiresInHours 쿠키 만료 시간 (시간 단위), 기본값으로 사용될 수 있음
 */
export const setUtmCookies = (utmParams?: ParsedUrlQuery, expiresInHours?: number) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!utmParams) {
    return;
  }

  // 현재 URL 경로가 쿠키 설정 제외 대상인지 확인
  if (isExcludedPath()) {
    return;
  }

  // Paid/Non-paid 구분 로직
  if (!isPaidTraffic(utmParams)) {
    // 3순위: 오가닉으로 간주 (skip)
    return;
  }

  // Paid 트래픽인 경우에만 쿠키 설정 진행
  const utmQueryParams = extractUtmParams(utmParams);

  // 쿠키 리셋 필요 여부 확인
  if (shouldResetUtmCookies(utmQueryParams)) {
    removeAllUtmCookies();

    const url = new URL(window.location.href);
    const origin = encodeURIComponent(url.origin + url.pathname);
    const nowMS = Date.now().toString();

    utmParams = { ...utmParams, utm_landing_page_url: origin, utm_entry_time: nowMS };
  }

  const expHours = expiresInHours ?? getExpirationHoursByUtmSource(utmParams.utm_source);

  const expires = new Date();
  expires.setHours(expires.getHours() + expHours);

  // 새로운 utm_* 쿠키 설정
  setUtmCookieValues(utmParams, expires);
};
